import { useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  Divider,
  Flex,
  HStack,
  IconButton,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
} from '@chakra-ui/react';
import { AddIcon, DeleteIcon } from '@chakra-ui/icons';

import SectionCard from '../../components/SectionCard';
import InsuredFields from './InsuredFields';
import {
  createEmptyInsured,
  createEmptyCorporationInsured,
  MAX_CO_INSUREDS,
  getRiskAddress,
} from '../../constants/locationOptions';
import { useAnswerCodes, ValidationEntityProvider } from '../../hooks';
import type { LocationComponentProps } from './types';
import type { Insured } from 'api';

interface InsuredCardProps extends LocationComponentProps {
  isUpdating?: boolean;
  customerType?: string;
}

const InsuredCard = ({
  location,
  index,
  isUpdating,
  onLocationChange,
  customerType,
}: InsuredCardProps) => {
  const { getOptions, getCode, isLoading } = useAnswerCodes();
  const occupationOptions = getOptions('hab_occupation');
  const {
    isOpen: isConsentModalOpen,
    onOpen: onOpenConsentModal,
    onClose: onCloseConsentModal,
  } = useDisclosure();

  const creditYes = getCode('Yes', 'credit_consent');
  const creditNo = getCode('No', 'credit_consent');
  const privacyYes = getCode('Yes', 'privacy_consent');
  const privacyNo = getCode('No', 'privacy_consent');

  const insured: Insured = location.insured || createEmptyInsured();
  const coInsureds: Insured[] = location.co_insureds || [];
  const riskAddress = useMemo(
    () => getRiskAddress(location),
    [location.address, location.city, location.province, location.postal_code],
  );

  const handleInsuredFieldChange = (
    field: string,
    value: string | boolean | null | number,
  ) => {
    onLocationChange(index, 'insured', value, field);
  };

  const handleInsuredUpdate = useCallback(
    (updatedInsured: Insured) => {
      onLocationChange(index, 'insured', updatedInsured);
    },
    [index, onLocationChange],
  );

  const handleCoInsuredsUpdate = useCallback(
    (updatedCoInsureds: Insured[]) => {
      onLocationChange(index, 'co_insureds', updatedCoInsureds);
    },
    [index, onLocationChange],
  );

  const handleCoInsuredFieldChange = (
    coInsuredIndex: number,
    field: string,
    value: string | boolean | null | number,
  ) => {
    const updatedCoInsureds = [...coInsureds];
    updatedCoInsureds[coInsuredIndex] = {
      ...updatedCoInsureds[coInsuredIndex],
      [field]: value,
    };
    handleCoInsuredsUpdate(updatedCoInsureds);
  };

  const handleCoInsuredUpdate = (
    coInsuredIndex: number,
    updatedInsured: Insured,
  ) => {
    const updatedCoInsureds = [...coInsureds];
    updatedCoInsureds[coInsuredIndex] = updatedInsured;
    handleCoInsuredsUpdate(updatedCoInsureds);
  };

  const handlePrimaryConsentChange = useCallback(
    (field: 'retrieve_credit' | 'privacy_consent', value: string) => {
      const yesCode = field === 'retrieve_credit' ? creditYes : privacyYes;
      const noCode = field === 'retrieve_credit' ? creditNo : privacyNo;

      const updatedInsured = { ...insured, [field]: value };

      if (value === yesCode && coInsureds.length > 0) {
        const updatedCoInsureds = coInsureds.map((ci) => ({
          ...ci,
          [field]: noCode,
        }));
        onLocationChange(index, {
          insured: updatedInsured,
          co_insureds: updatedCoInsureds,
        });
      } else {
        onLocationChange(index, { insured: updatedInsured });
      }
    },
    [
      index,
      onLocationChange,
      insured,
      coInsureds,
      creditYes,
      creditNo,
      privacyYes,
      privacyNo,
    ],
  );

  const handleCoInsuredConsentChange = useCallback(
    (
      coInsuredIndex: number,
      field: 'retrieve_credit' | 'privacy_consent',
      value: string,
    ) => {
      const yesCode = field === 'retrieve_credit' ? creditYes : privacyYes;
      const noCode = field === 'retrieve_credit' ? creditNo : privacyNo;

      const updatedCoInsureds = [...coInsureds];
      updatedCoInsureds[coInsuredIndex] = {
        ...updatedCoInsureds[coInsuredIndex],
        [field]: value,
      };

      if (value === yesCode) {
        const updatedInsured = { ...insured, [field]: noCode };

        updatedCoInsureds.forEach((ci, i) => {
          if (i !== coInsuredIndex) {
            updatedCoInsureds[i] = { ...ci, [field]: noCode };
          }
        });

        onLocationChange(index, {
          insured: updatedInsured,
          co_insureds: updatedCoInsureds,
        });
      } else {
        onLocationChange(index, { co_insureds: updatedCoInsureds });
      }
    },
    [
      index,
      onLocationChange,
      insured,
      coInsureds,
      creditYes,
      creditNo,
      privacyYes,
      privacyNo,
    ],
  );

  const handleAddCoInsured = () => {
    if (coInsureds.length >= MAX_CO_INSUREDS) return;
    const newCoInsured = {
      ...createEmptyInsured(),
      mailing_address: riskAddress.address,
      mailing_city: riskAddress.city,
      mailing_province: riskAddress.province,
      mailing_postal_code: riskAddress.postal_code,
    };
    handleCoInsuredsUpdate([...coInsureds, newCoInsured]);
  };

  const handleAddCorporation = () => {
    if (coInsureds.length >= MAX_CO_INSUREDS) return;
    const newCorporation = {
      ...createEmptyCorporationInsured(),
      mailing_address: riskAddress.address,
      mailing_city: riskAddress.city,
      mailing_province: riskAddress.province,
      mailing_postal_code: riskAddress.postal_code,
    };
    handleCoInsuredsUpdate([...coInsureds, newCorporation]);
  };

  const handleDeleteCoInsured = (coInsuredIndex: number) => {
    handleCoInsuredsUpdate(coInsureds.filter((_, i) => i !== coInsuredIndex));
  };

  const isDisabled = isUpdating;

  return (
    <ValidationEntityProvider
      entity={location as unknown as Record<string, unknown>}
    >
      <SectionCard title="Insured" mt={6}>
        <InsuredFields
          insured={insured}
          riskAddress={riskAddress}
          occupationOptions={occupationOptions}
          isLoading={isLoading}
          isDisabled={isDisabled || false}
          onFieldChange={handleInsuredFieldChange}
          onConsentChange={handlePrimaryConsentChange}
          onInsuredUpdate={handleInsuredUpdate}
          onOpenConsentModal={onOpenConsentModal}
        />

        <Divider my={6} />

        {coInsureds.map((coInsured, coInsuredIndex) => (
          <Box key={coInsuredIndex} mb={6}>
            <Flex justify="space-between" align="center" mb={3}>
              <Text fontSize="sm" fontWeight="600">
                {coInsured.is_corporation
                  ? `Corporation ${coInsuredIndex + 1}`
                  : `Co-Insured ${coInsuredIndex + 1}`}
              </Text>
              <IconButton
                aria-label="Delete co-insured"
                icon={<DeleteIcon />}
                size="sm"
                variant="ghost"
                colorScheme="red"
                onClick={() => handleDeleteCoInsured(coInsuredIndex)}
                isDisabled={isDisabled}
              />
            </Flex>
            <InsuredFields
              insured={coInsured}
              riskAddress={riskAddress}
              occupationOptions={occupationOptions}
              isLoading={isLoading}
              isDisabled={isDisabled || false}
              isCoInsured
              onFieldChange={(field, value) =>
                handleCoInsuredFieldChange(coInsuredIndex, field, value)
              }
              onConsentChange={(field, value) =>
                handleCoInsuredConsentChange(coInsuredIndex, field, value)
              }
              onInsuredUpdate={(updated) =>
                handleCoInsuredUpdate(coInsuredIndex, updated)
              }
              onOpenConsentModal={onOpenConsentModal}
            />
            {coInsuredIndex < coInsureds.length - 1 && <Divider mt={6} />}
          </Box>
        ))}

        <HStack spacing={2}>
          <Button
            leftIcon={<AddIcon />}
            variant="solid"
            colorScheme="yellow"
            size="sm"
            onClick={handleAddCoInsured}
            isDisabled={isDisabled || coInsureds.length >= MAX_CO_INSUREDS}
          >
            Add Named Insured
          </Button>
          {customerType === 'corporation' && (
            <Button
              leftIcon={<AddIcon />}
              variant="solid"
              colorScheme="gray"
              size="sm"
              onClick={handleAddCorporation}
              isDisabled={isDisabled || coInsureds.length >= MAX_CO_INSUREDS}
            >
              Add Corporation
            </Button>
          )}
        </HStack>

        <Modal
          isOpen={isConsentModalOpen}
          onClose={onCloseConsentModal}
          size="lg"
        >
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Consent Statement</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Text mb={4}>
                As your broker, I am gathering this consent on behalf of the
                insurer who advises me that: in respect of this
                quotation/application, any policy we issue and subsequent
                renewals or changes, we may collect, use and disclose personal
                information reports including policy history, claims history,
                investigative information, rating information, risk information
                and only with respect to you, your personal credit information
                including credit score, as permitted by law and in accordance
                with our privacy policies.
              </Text>
              <Text>
                Do you, and on behalf of others listed, authorize us to collect,
                use and disclose to appropriate third parties, including your
                broker, this information solely relating to your personal
                property policy for the purposes necessary to assess the risk,
                determine a premium, determine eligibility and conditions for a
                premium payment plan, investigate and settle claims, analyze
                business results and detect and prevent fraud?
              </Text>
            </ModalBody>
            <ModalFooter>
              <Button onClick={onCloseConsentModal}>Close</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </SectionCard>
    </ValidationEntityProvider>
  );
};

export default InsuredCard;
